import axios from 'axios'
import Vue from 'vue'

export default {
  namespaced: true,

  state () {
    return {
      // common
      checklistId: null,
      language: '',
      languages: [
        { code: '', name: 'default' },
        { code: 'cs', name: 'Čeština' },
        { code: 'de', name: 'Deutsch' },
        { code: 'en', name: 'English' },
        { code: 'fr', name: 'Français' },
        { code: 'it', name: 'Italiano' },
        { code: 'nl', name: 'Nederlands' },
        { code: 'no', name: 'Norsk' },
        { code: 'pl', name: 'Polski' },
        { code: 'sv', name: 'Svenska' },
        { code: 'sk', name: 'Slovenčina' }
      ],

      // editor
      rootSection: {
        idx: 0,
        lvl: 0,
        type: 'section',
        items: []
      },
      copy: null,
      cut: null,
      deviceType: null,
      isFilled: false,

      // checklist
      interventionId: null,
      results: {},
      brcFiles: [],
      brcData: {},
      isLocked: false
    }
  },

  getters: {
    // common
    checklistId (state) {
      return state.checklistId
    },

    language (state) {
      return state.language
    },

    languages (state) {
      return state.languages
    },

    // editor
    root (state) {
      return state.rootSection
    },

    nextIdx (state) {
      return findMaxIdx(state.rootSection) + 1
    },

    inCopy: (state) => (idx) => {
      return state.copy === idx
    },

    copyIdx (state) {
      return state.copy
    },

    inCut: (state) => (idx) => {
      return state.cut === idx
    },

    cutIdx (state) {
      return state.cut
    },

    deviceType (state) {
      return state.deviceType
    },

    isFilled (state) {
      return state.isFilled
    },

    // checklist
    interventionId (state) {
      return state.interventionId
    },

    isLocked (state) {
      return state.isLocked
    },

    results (state) {
      return state.results
    },

    value: (state) => (idx) => {
      if (state.results[idx] === undefined) {
        return null
      }
      return state.results[idx].value
    },

    brcFiles (state) {
      return state.brcFiles
    },

    brcData: (state) => (file) => {
      return state.brcData[file]
    },

    units () {
      return [
        { value: null, label: 'default' },
        { value: 'f', label: 'Frequency' },
        { value: 'V', label: 'Voltage' },
        { value: 'A', label: 'Current' },
        { value: 'F', label: 'Capacity' },
        { value: 'R', label: 'Resistivity' },
        { value: 'L', label: 'Length' },
        { value: 'P', label: 'Percentage' }
      ]
    },

    displayUnits () {
      return {
        'f': ['Hz', 'kHz'],
        'V': ['mV', 'V', 'kV'],
        'A': ['mA', 'A', 'kA'],
        'F': ['uF'],
        'R': ['Ω', 'kΩ'],
        'L': ['mm', 'm', 'km'],
        'P': ['%']
      }
    },

    receiveUnit: () => (unit) => {
      const receiveUnits = {
        'f': 'Hz',
        'V': 'V',
        'A': 'A',
        'F': 'uF',
        'R': 'Ω',
        'L': 'm',
        'P': '%'
      }
      return receiveUnits[unit] || null
    }
  },

  mutations: {
    // common
    setChecklistId (state, data) {
      const { id } = data
      state.checklistId = id
    },

    setLanguage (state, data) {
      const { language } = data
      state.language = language
    },

    // editor
    restoreEditor (state, data) {
      state.rootSection = data || {}
    },

    updateItem (state, data) {
      update(state.rootSection, data)
    },

    insertItem (state, data) {
      const { idx, item } = data
      insert(state.rootSection, idx, item)
    },

    removeItem (state, data) {
      const { idx } = data
      remove(state.rootSection, idx)
    },

    setCopyIdx (state, data) {
      const { idx } = data
      state.copy = idx
    },

    setCutIdx (state, data) {
      const { idx } = data
      state.cut = idx
    },

    setDeviceType (state, data) {
      const { deviceType } = data
      state.deviceType = deviceType || null
    },

    setIsFilled (state, data) {
      const { isFilled } = data
      state.isFilled = isFilled
    },

    // checklist
    setInterventionId (state, data) {
      const { id } = data
      state.interventionId = id
    },

    restoreResults (state, data = {}) {
      const results = {}
      // sanitize "flat" legacy results
      Object.keys(data).forEach((idx) => {
        if (/object/.test(typeof data[idx]) && !Array.isArray(data[idx]) && data[idx] !== null) {
          results[idx] = data[idx]
        } else {
          results[idx] = {
            value: data[idx],
            note: null
          }
        }
      })
      Vue.set(state, 'results', results)
    },

    setValue (state, { idx, value, valid, subIds }) {
      // set input value
      const prev = state.results[idx] || {}
      Vue.set(state.results, idx, { ...prev, value, valid })
      // clear fallback values
      if (valid && subIds && subIds.length) {
        subIds.forEach((idx) => { delete state.results[idx] })
      }
    },

    setNote (state, { idx, note }) {
      const prev = state.results[idx] || {}
      Vue.set(state.results, idx, { ...prev, note })
    },

    lock (state) {
      state.isLocked = true
    },

    unlock (state) {
      state.isLocked = false
    },

    save() {
      // doin' nothing: used for autosave feature
    }
  },

  actions: {
    // common
    async fetchImage ({ commit, rootState }, { id, requiredSize }) {
      const size = `${requiredSize || 'orig'}`
      // already in store
      if (rootState.files[id] && rootState.files[id][size]) {
        return Promise.resolve()
      }
      // load from server
      try {
        const response = await axios({
          method: 'GET',
          url: '/files/' + id + '/download-full' + (requiredSize ? ('?size=' + requiredSize) : '')
        })

        const { file, data } = response.data
        const load = `data:image/${file.extension.replace('.', '')};base64,${data}`

        commit('addManyItems', {
          modelName: 'files',
          items: [file]
        }, {
          root: true
        })

        if (!rootState.files[id]) {
          Vue.set(rootState.files, id, { [size]: load })
        }
        if (!rootState.files[id][size]) {
          Vue.set(rootState.files, id, { ...rootState.files[id], [size]: load })
        }

        return Promise.resolve()
      } catch (error) {
        return Promise.resolve(error) // TODO: temporaty fix
      }
    },

    // editor
    async clone ({}, { id, name, code, version }) {
      try {
        const url = `/checklists/${id}/clone`
        const response = await axios.post(url, {
          name,
          code,
          version
        })
        return Promise.resolve({ id: response.data.id })
      } catch (error) {
        return Promise.reject(error)
      }
    },

    async uploadEditorImage ({ getters }, { formData }) {
      try {
        const url = `/files?modelName=checklist&checklistId=${getters.checklistId}&resize=true`
        const response = await axios.post(url, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        return Promise.resolve({ id: response.data.fileId })
      } catch (error) {
        return Promise.reject(error)
      }
    },

    async removeEditorImage ({ dispatch }, { id, fileInId }) {
      try {
        if (fileInId) await dispatch('destroyItem', {
          model: { name: 'files-in-checklists' },
          id: fileInId
        }, {
          root: true
        })
        if (id) await dispatch('destroyItem', {
          model: { name: 'files' },
          id
        }, {
          root: true
        })
        return Promise.resolve()
      } catch (error) {
        return Promise.reject(error)
      }
    },

    copy ({ commit }, data) {
      commit('setCutIdx', { idx: null })
      commit('setCopyIdx', data)
    },

    cut ({ commit }, data) {
      commit('setCopyIdx', { idx: null })
      commit('setCutIdx', data)
    },

    paste ({ state, getters, commit }, data) {
      const { copy, cut } = state
      const itemIdx = copy || cut
      let item

      if (copy) {
        item = updateIdx(
          toJson(getItem(state.rootSection, itemIdx)),
          () => getters.nextIdx
        )
      }

      if (cut) {
        item = toJson(getItem(state.rootSection, itemIdx))
        commit('removeItem', { idx: itemIdx })
      }

      commit('insertItem', { idx: data.idx, item })
      commit('setCopyIdx', { idx: null })
      commit('setCutIdx', { idx: null })
    },

    // checklist
    async uploadInputImage ({ getters }, { formData }) {
      try {
        const url = `/files?modelName=checklist-in-intervention&interventionId=${getters.interventionId}&checklistId=${getters.checklistId}&resize=true`
        const response = await axios.post(url, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        return Promise.resolve({ id: response.data.fileId })
      } catch (error) {
        return Promise.reject(error)
      }
    },

    async removeInputImage ({ dispatch }, { id, fileInId }) {
      try {
        await dispatch('destroyItem', {
          model: { name: 'files-in-checklists-in-interventions' },
          id: fileInId
        }, {
          root: true
        })
        await dispatch('destroyItem', {
          model: { name: 'files' },
          id
        }, {
          root: true
        })
        return Promise.resolve()
      } catch (error) {
        return Promise.reject(error)
      }
    },

    async fetchBrcFiles ({ state }, { assetId }) {
      try {
        const url = `/assets/${assetId}/get-brclab-files`
        const response = await axios.get(url)
        if (!response.data) {
          return Promise.reject(new Error('No BRC files provided'))
        }
        state.brcFiles = response.data
        return Promise.resolve()
      } catch (error) {
        return Promise.reject(error)
      }
    },

    async fetchBrcData ({ state }, { assetId, file }) {
      try {
        const url = `/assets/${assetId}/get-brclab-data?filename=${file}`
        const response = await axios.get(url)
        if (!response.data) {
          return Promise.reject(new Error('No BRC data provided'))
        }
        state.brcData = Object.assign({}, state.brcData, { [file]: response.data })
        return Promise.resolve()
      } catch (error) {
        return Promise.reject(error)
      }
    },

    importValues ({ commit }, { data, checklist }) {
      fill(scan(checklist), data).forEach((data) => {
        commit('setValue', data)
      })
    }
  }
}

// Helpers

function toJson (obj) {
  return JSON.parse(JSON.stringify(obj))
}

function transformValue (value, source) {
  if (!source || !(source.receiveUnit && source.displayUnit)) {
    return value
  }

  const { receiveUnit, displayUnit } = source

  if (receiveUnit === displayUnit) {
    return value
  }

  const power = ['u', 'm', 'x', 'k', 'M']
  const regex = /^(u|m|k|M)?(Hz|V|A|F|Ω|m|%)$/
  const [ , receivePrefix ] = receiveUnit.match(regex)
  const [ , displayPrefix ] = displayUnit.match(regex)
  const rpi = power.indexOf(receivePrefix)
  const dpi = power.indexOf(displayPrefix)
  const receivePower = rpi < 0 ? 2 : rpi
  const displayPower = dpi < 0 ? 2 : dpi

  if (receivePower > displayPower) {
    return roundValue(value * Math.pow(1000, receivePower - displayPower))
  }
  if (receivePower < displayPower) {
    return roundValue(value * (1 / Math.pow(1000, displayPower - receivePower)))
  }
}

function roundValue(value, decimals = 10) {
  const d = Math.pow(10, decimals)
  return Math.round(value * d) / d
}

function scan (obj) {
  let res = []

  if (obj.source) {
    res.push({
      idx: obj.idx,
      type: obj.type,
      source: obj.source
    })
  }

  let items = obj.items
  if (items) {
    for (let obj of items) {
      let sub = scan(obj)
      if (sub.length) {
        res = res.concat(sub)
      }
    }
  }

  return res
}

function fill (scan, data) {
  let keys = Object.keys(data)

  return scan.reduce((res, item) => {
    let { source, type, idx } = item
    if (keys.includes(source.name)) {
      let value = sanitize(data[source.name], type)
      if (value !== null) {
        res.push({
          idx: idx,
          value: transformValue(value, source)
        })
      }
    }
    return res
  }, [])
}

function sanitize (value, type) {
  let vt = typeof value
  if ((/undefined/).test(vt)) return null
  if ((/string/).test(type)) return String(value)
  if ((/number/).test(type)) return Number(value)
  if ((/switch/).test(type)) {
    if ((/number|boolean/).test(vt)) return Boolean(value)
    if ((/string/).test(vt)) return !(/0|f(alse)?|n(o)?/i).test(value)
    if ((/object/).test(vt)) return !(isNaN(value) || value === null)
  }
  return value
}

function updateIdx (obj, nextIdx) {
  if (obj.idx) {
    obj.idx = nextIdx()
  }
  if (obj.items && obj.items.length) {
    for (let item of obj.items) {
      updateIdx(item, nextIdx)
    }
  }
  return obj
}

function getItem (obj, idx) {
  if (obj.idx === idx) {
    return obj
  }
  if (obj.items === undefined) {
    return null
  }
  for (let item of obj.items) {
    let result = getItem(item, idx)
    if (result) {
      return result
    }
  }
}

function findMaxIdx (obj) {
  let maxIdx = obj.idx || 0
  if (obj.items && obj.items.length) {
    for (let item of obj.items) {
      let idx = findMaxIdx(item)
      if (idx > maxIdx) {
        maxIdx = idx
      }
    }
  }
  return maxIdx
}

function update (obj, val) {
  if (val.idx === undefined) {
    return null
  }

  if (obj.idx !== undefined && obj.idx === val.idx) {
    return Object.assign(obj, val)
  }

  let items = obj.items
  if (items) {
    for (let obj of items) {
      let res = update(obj, val)
      if (res) {
        return res
      }
    }
  }

  return null
}

function insert (obj, idx, item) {
  if (obj.idx !== undefined && obj.idx === idx) {
    obj.items.push(item)
    return item
  }

  let items = obj.items
  if (items) {
    for (let obj of items) {
      let res = insert(obj, idx, item)
      if (res) {
        return res
      }
    }
  }

  return null
}

function remove (obj, idx) {
  let items = obj.items
  if (items) {
    let i = 0
    for (let obj of items) {
      if (obj.idx === idx) {
        items.splice(i, 1)
        return obj
      } else if (obj.items) {
        let res = remove(obj, idx)
        if (res) {
          return res
        }
      }
      ++i
    }
  }
  return null
}
